/* eslint-disable react/destructuring-assignment */
import { GetStaticPaths, GetStaticProps } from 'next';
import ErrorPage from 'pages/_error';
import { Article, Content, ContentType, Event } from 'pleinchamp-api-client';
import React, { createContext } from 'react';
import { useSelector } from 'react-redux';
import { ContentApi } from '@api/business/api.utils';
import { EventLayout } from '@events/components/EventLayout/EventLayout.component';
import { searchContent } from '@search/business/search.utils';
import { userSelectors } from '@user/business/User.store';
import { ApiStatusCode, formatPropsForStatic } from '@utils/api.utils';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString } from '@utils/url';
import { PlcNextPage, PlcPageContextProps } from 'features/business/app.types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const NAMESPACES = [...defaultNamespaces, 'event'];
interface Props {
  slug: string;
  event: Event;
  readAlso: Article[];
  isEventValidating: boolean;
}

async function fetchEvent(slug: Props['slug']) {
  const r = await ContentApi.getEvent(slug);
  return r.data;
}

async function fetchReadAlso(slug: Props['slug']) {
  const r = await ContentApi.getRelatedEvents(slug);
  return r.data;
}

export const EventContext = createContext<PlcPageContextProps<Props>>({
  event: { initialData: undefined },
  isEventValidating: { data: true },
  readAlso: { isValidating: false },
  slug: { isValidating: false },
});

const EventPage: PlcNextPage<Partial<Props>> = (props) => {
  const isInit = useSelector(userSelectors.isInit);
  const isPro = useSelector(userSelectors.isPro) || undefined;
  const isProArticle = Boolean(props.event?.isPro);

  const waitForEvent = isPro && isProArticle && !isInit;

  if (!props.event) {
    return <ErrorPage errorCode={ApiStatusCode.notFound} />;
  }

  return (
    <EventContext.Provider
      value={{
        event: { initialData: props.event },
        isEventValidating: { isValidating: waitForEvent },
        readAlso: { data: props.readAlso },
        slug: { data: props.slug },
      }}
    >
      <EventLayout />
    </EventContext.Provider>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  let event: Content[];
  try {
    event = await searchContent({ contentTypes: [ContentType.Event], isPro: false, limit: 20 }).then((r) => {
      return r.contents;
    });
  } catch {
    event = [];
  }
  const paths = event.map((a) => ({
    params: { slug: a.slug },
  }));

  return {
    fallback: true,
    paths,
  };
};

export const getStaticProps: GetStaticProps | { redirect: { destination: string; permanent: boolean } } = async ({
  params,
  locale,
}) => {
  const slug = castUrlParamToString(params?.slug);

  if (!slug || slug === '') {
    console.error('Error actualite StaticPropsSide, slug worth : ', slug);
    return <ErrorPage errorCode={ApiStatusCode.notFound} />;
  }

  const event = await fetchEvent(slug)
    .then((result) => result)
    .catch((error: { response: { status: ApiStatusCode } }) => {
      console.error('Error fetchArticle actualite on StaticProps', slug, error);
      if (error && error.response && error.response.status && error.response.status === ApiStatusCode.notFound) {
        return ApiStatusCode.notFound;
      }
      return undefined;
    });

  const props = await Promise.all([fetchReadAlso(slug), serverSideTranslations(locale || 'fr', defaultNamespaces)])
    .then(([readAlso, i18n]) => ({
      event,
      readAlso,
      ...i18n,
    }))
    .catch(async (err) => {
      console.error('Error occured on /index.tsx', err);
      return serverSideTranslations(locale || 'fr', NAMESPACES);
    });
  return formatPropsForStatic(props);
};

export default EventPage;
